import React, { useEffect, useState } from "react";
import { auth, db } from "../firebase";
import { Link, useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { doc, getDoc } from "firebase/firestore";
import { LogOut, User, LayoutDashboard, Shield } from "lucide-react";

const Navbar = () => {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [displayName, setDisplayName] = useState("");
  const [userType, setUserType] = useState(""); // "doctor", "patient", or "admin"
  const [loadingRole, setLoadingRole] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const fetchUserRole = async () => {
      // Reset state when user logs out
      if (!user) {
        setUserType("");
        setDisplayName("");
        setLoadingRole(false);
        return;
      }

      // Reset state when user changes
      setUserType("");
      setDisplayName("");
      setLoadingRole(true);

      try {
        // First check if user is a pure admin (in admins collection)
        const adminRef = doc(db, "admins", user.uid);
        const adminSnap = await getDoc(adminRef);

        if (adminSnap.exists()) {
          const adminData = adminSnap.data();
          setUserType("admin");
          setDisplayName(adminData.name || user.email || "Admin");
          return;
        }

        // Then check if user is in doctors collection
        const doctorRef = doc(db, "doctors", user.uid);
        const doctorSnap = await getDoc(doctorRef);

        if (doctorSnap.exists()) {
          const doctorData = doctorSnap.data();
          // Check if doctor has admin role
          if (doctorData.role === "admin") {
            setUserType("admin");
            setDisplayName(doctorData.name || user.email || "Admin");
            return;
          } else {
            setUserType("doctor");
            setDisplayName({ name: doctorData.name, title: "Dr." });
            return;
          }
        }

        // If not a doctor, check if patient
        const patientRef = doc(db, "patients_auth", user.uid);
        const patientSnap = await getDoc(patientRef);

        if (patientSnap.exists()) {
          setUserType("patient");
          setDisplayName(patientSnap.data().fullName || "Patient");
        } else {
          // User exists but not found in either collection
          setUserType("");
          setDisplayName("");
        }
      } catch (err) {
        console.error("Error fetching user type:", err);
        setUserType("");
        setDisplayName("");
      } finally {
        setLoadingRole(false);
      }
    };

    fetchUserRole();
  }, [user]);

  const handleLogout = async () => {
    await auth.signOut();
    navigate("/login");
  };

  const formattedName = typeof displayName === 'object' ? `${displayName.title} ${displayName.name}` : displayName;

  return (
    <nav className="navbar sticky top-0 z-50 bg-gradient-to-r from-teal-50/90 via-white/90 to-white/90 backdrop-blur-sm border-b border-teal-100 shadow-sm transition-all duration-300">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo Section */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center gap-2 group">
              <img
                src="/logo-green.png"
                alt="PulseNote"
                className="w-9 h-9 transition-transform group-hover:scale-105"
              />
              <h1 className="text-2xl font-bold bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent">
                PulseNote
              </h1>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            {!user ? (
              <>
                <Link
                  to="/login"
                  className="text-gray-600 hover:text-teal-600 font-medium px-3 py-2 transition-colors"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white px-5 py-2 rounded-lg font-medium shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
                >
                  Register
                </Link>
              </>
            ) : (
              <>
                {userType === "doctor" && (
                  <Link
                    to="/dashboard"
                    className="flex items-center gap-2 text-gray-600 hover:text-teal-600 hover:bg-teal-50 px-3 py-2 rounded-lg transition-all"
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    <span>Dashboard</span>
                  </Link>
                )}

                {userType === "admin" && (
                  <Link
                    to="/admin"
                    className="flex items-center gap-2 text-gray-600 hover:text-teal-600 hover:bg-teal-50 px-3 py-2 rounded-lg transition-all"
                  >
                    <Shield className="w-4 h-4" />
                    <span>Admin Panel</span>
                  </Link>
                )}

                {userType === "patient" && (
                  <Link
                    to="/patient/portal"
                    className="flex items-center gap-2 text-gray-600 hover:text-teal-600 hover:bg-teal-50 px-3 py-2 rounded-lg transition-all"
                  >
                    <User className="w-4 h-4" />
                    <span>My Portal</span>
                  </Link>
                )}

                <div className="h-6 w-px bg-gray-200 mx-2"></div>

                {userType === "doctor" ? (
                  <button
                    onClick={() => navigate("/doctor/profile")}
                    className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full hover:bg-teal-50 transition-all group border border-transparent hover:border-teal-100"
                  >
                    <div className="w-7 h-7 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center group-hover:bg-teal-200 transition-colors">
                      <User className="w-4 h-4" />
                    </div>
                    <span className="text-gray-700 font-semibold text-sm group-hover:text-teal-700 transition-colors">
                      {formattedName || "Doctor"}
                    </span>
                  </button>
                ) : (
                  <span className="text-gray-700 font-medium text-sm">
                    {loadingRole ? "..." : formattedName || user.email}
                  </span>
                )}

                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 bg-gray-100 hover:bg-red-50 text-gray-700 hover:text-red-600 p-2 rounded-full transition-colors ml-1"
                  title="Logout"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            )}
          </div>

          {/* Mobile Menu Button - Keeping it simple for now, can be expanded */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-600 hover:text-teal-600 p-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-100 bg-white">
          <div className="px-4 pt-2 pb-4 space-y-2">
            {!user ? (
              <div className="flex flex-col gap-2 mt-2">
                <Link to="/login" className="block w-full text-center py-2 text-gray-600 font-medium bg-gray-50 rounded-lg">Login</Link>
                <Link to="/register" className="block w-full text-center py-2 text-white font-medium bg-teal-600 rounded-lg">Register</Link>
              </div>
            ) : (
              <>
                <div className="py-2 border-b border-gray-100 mb-2">
                  <p className="text-sm text-gray-500">Signed in as</p>
                  <p className="font-semibold text-gray-900">{formattedName || user.email}</p>
                </div>

                {userType === "doctor" && (
                  <>
                    <Link to="/dashboard" className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-teal-700 hover:bg-teal-50">Dashboard</Link>
                    <Link to="/doctor/profile" className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-teal-700 hover:bg-teal-50">Profile</Link>
                  </>
                )}

                <button
                  onClick={handleLogout}
                  className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-red-600 hover:bg-red-50"
                >
                  Logout
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
